import type { Components } from '@/api/generated/api';
import { useCompetenceDomain } from '@/composables/useCompetenceDomain';
import { useStudentRecipientLabel } from '@/composables/useStudentRecipientLabel';
import { findFocusedStudent } from '@/utils/students.utils';
import { MAX_NAMED_ACTIVITIES } from 'common/constants/support.measure.constants';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { activityTargetKind, type SupportMeasureActivity } from './supportMeasureActivity.utils';

// bold subject parts (individual names / a single count) joined as a human list, plus a greyed verb —
// e.g. names ["Karin", "Bernd"] + verb "entfernt" renders "Karin und Bernd entfernt"
export interface IActivityCompactLabel {
  /** "+n weitere" when a focused student stands for a larger group. */
  additional: string | null;
  names: string[];
  verb: string;
}

export interface IUseSupportMeasureActivityDisplay {
  compactLabel: (activities: SupportMeasureActivity[], focusedStudentId?: number | null) => IActivityCompactLabel;
}

export const useSupportMeasureActivityDisplay = (): IUseSupportMeasureActivityDisplay => {
  const { t } = useI18n();
  const { getStudentRecipientLabel } = useStudentRecipientLabel();
  const { allCompetenceDomains } = useCompetenceDomain();

  const competenceDescById = computed<Map<number, string>>(() => {
    const map = new Map<number, string>();
    for (const domain of allCompetenceDomains.value) {
      for (const competence of domain.competences) map.set(competence.id, competence.description);
    }
    return map;
  });

  const verbOf = (activity: SupportMeasureActivity): string => {
    switch (activity.type) {
      case 'studentAdded':
        return t('supportMeasureEntry.activityVerb.added');
      case 'studentRemoved':
        return t('supportMeasureEntry.activityVerb.removed');
      case 'studentEntriesRemoved':
        return t('supportMeasureEntry.activityVerb.entriesRemoved');
      case 'competenceAdded':
        return t('supportMeasureEntry.activityVerb.linked');
      case 'competenceRemoved':
        return t('supportMeasureEntry.activityVerb.unlinked');
      default:
        return '';
    }
  };

  // Students read exactly like the labels next to avatars, so both surfaces stay in sync.
  const studentLabel = (
    activities: SupportMeasureActivity[],
    focusedStudentId: number | null,
  ): Pick<IActivityCompactLabel, 'additional' | 'names'> => {
    // The activity carries its student (the row is cascade-deleted with them), so a student who left the class
    // or the measure is still named — no roster lookup involved.
    const students = activities
      .map((activity) => activity.student)
      .filter((student): student is Components.Schemas.BaseStudentDto => student != null);

    // Should a row ever lack its student, count over the full group rather than silently drop names.
    if (students.length !== activities.length) {
      // same key as the shared rule, so a counted group reads identically wherever it appears
      return { additional: null, names: [t('general.studentCount', { count: activities.length })] };
    }

    const { additionalLabel, labelParts } = getStudentRecipientLabel({
      focusedStudent: findFocusedStudent(students, focusedStudentId),
      students,
    });
    return { additional: additionalLabel, names: labelParts };
  };

  const competenceNames = (activities: SupportMeasureActivity[]): string[] => {
    const descriptions = activities
      .map((activity) =>
        activity.competenceId == null ? undefined : competenceDescById.value.get(activity.competenceId),
      )
      .filter((description): description is string => Boolean(description));

    // individual descriptions (joined by a normal-weight "und") only when every activity resolves
    const allResolved = descriptions.length === activities.length;
    return allResolved && activities.length <= MAX_NAMED_ACTIVITIES
      ? descriptions
      : [t('supportMeasureEntry.activityCount.competences', { count: activities.length })];
  };

  const compactLabel = (
    activities: SupportMeasureActivity[],
    focusedStudentId: number | null = null,
  ): IActivityCompactLabel => {
    const first = activities[0];
    // Groups never mix kinds (the grouping key includes the target kind), so the first activity decides.
    if (activityTargetKind(first) === 'competence') {
      return { additional: null, names: competenceNames(activities), verb: verbOf(first) };
    }
    return { ...studentLabel(activities, focusedStudentId), verb: verbOf(first) };
  };

  return { compactLabel };
};
